import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Stack,
  Switch,
  Text,
  useToast,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  HStack,
  Image,
  Badge,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { Product, Pack } from "../../../entity/Product";
import { useSelector } from "react-redux";
import { selectStore } from "../../../store/selectors/StoreSelector";
import { WSMedia } from "../../../services/remote/media/WSMedia";
import { useDropzone } from "react-dropzone";
import { FiUpload, FiX, FiPlus, FiTrash2, FiPackage } from "react-icons/fi";
import { SimpleGrid, Spinner } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import VEditor from "../../components/veditor/VEditor";
import { DeleteIcon } from "@chakra-ui/icons";

interface CreateProductProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (product: Product) => Promise<void>;
  onUpdate?: (product: Product) => Promise<void>;
  product?: Product | null;
}

/**
 * Self-contained dropzone for a single pack's image.
 * Each pack card gets its own useDropzone instance, so dropping a file
 * here can never leak into the product gallery or another pack.
 */
interface PackImageUploadProps {
  imageUrl?: string;
  isUploading: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
  t: (key: string) => string;
}

const PackImageUpload: React.FC<PackImageUploadProps> = ({
  imageUrl,
  isUploading,
  onUpload,
  onRemove,
  t,
}) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".webp"],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        onUpload(acceptedFiles[0]);
      }
    },
  });

  if (isUploading) {
    return (
      <Flex
        borderWidth={2}
        borderStyle="dashed"
        borderColor="gray.200"
        borderRadius="md"
        p={6}
        align="center"
        justify="center"
        direction="column"
        bg="gray.50"
      >
        <Spinner size="sm" color="blue.500" mb={2} />
        <Text fontSize="xs" color="gray.500">
          {t("common.uploading")}
        </Text>
      </Flex>
    );
  }

  if (imageUrl) {
    return (
      <HStack spacing={3} align="center">
        <Image
          src={imageUrl}
          alt={t("product.pack_item_name")}
          boxSize="72px"
          objectFit="cover"
          borderRadius="md"
          borderWidth={1}
          borderColor="gray.200"
        />
        <IconButton
          aria-label={t("common.removeLogo")}
          icon={<DeleteIcon />}
          onClick={onRemove}
          colorScheme="red"
          variant="outline"
          size="sm"
        />
      </HStack>
    );
  }

  return (
    <Box
      {...getRootProps()}
      borderWidth={2}
      borderColor={isDragActive ? "blue.400" : "gray.200"}
      borderStyle="dashed"
      borderRadius="md"
      p={4}
      textAlign="center"
      cursor="pointer"
      bg={isDragActive ? "blue.50" : "transparent"}
      _hover={{ borderColor: "blue.400", bg: "blue.50" }}
      transition="all 0.15s"
    >
      <input {...getInputProps()} />
      <Icon as={FiUpload} w={5} h={5} color="gray.400" mb={1} />
      <Text fontSize="xs" color="gray.500">
        {isDragActive ? t("common.dropImageHere") : t("common.dragDropImage")}
      </Text>
    </Box>
  );
};

export const CreateAndUpdateProduct = ({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  product,
}: CreateProductProps) => {
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  const { t } = useTranslation();
  const mediaInstance = WSMedia.getInstance();
  const [isLoading, setIsLoading] = useState(false);
  const [removedImages, setremovedImages] = useState<string[]>([]);
  // Tracks which pack (by index) is currently uploading its image,
  // so each pack card can show its own spinner independently.
  const [uploadingPackIndex, setUploadingPackIndex] = useState<number | null>(
    null,
  );
  const store = useSelector(selectStore);

  const [formData, setFormData] = useState<Product>(
    product || {
      id: "",
      name: "",
      description: "",
      price: 0,
      discount: 0,
      quantity: 0,
      ref: "",
      imageUrl: [],
      inStock: true,
      deliveryPrice: 0,
      productCost: 0,
      storeId: store?.id!!,
      packName: "",
      packs: [],
    },
  );

  const toast = useToast();

  useEffect(() => {
    const fetchUrl = async () => {
      try {
        const url = await mediaInstance.getGeneratedURL();
        setGeneratedUrl(url);
      } catch (error) {
        console.error("Failed to get generated URL:", error);
      }
    };

    fetchUrl();
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (product) {
        setFormData(product);
      } else {
        setFormData({
          id: "",
          name: "",
          description: "",
          price: 0,
          discount: 0,
          quantity: 0,
          ref: "",
          imageUrl: [],
          inStock: true,
          deliveryPrice: 0,
          productCost: 0,
          storeId: store?.id!!,
          packName: "",
          packs: [],
        });
      }
      setremovedImages([]);
      setUploadingPackIndex(null);
    }
  }, [isOpen, product, store?.id]);

  const uploadPackImage = async (index: number, file: File) => {
    try {
      setUploadingPackIndex(index);
      const newUrl = await mediaInstance.getGeneratedURL();
      const imageUrl = await mediaInstance.saveImage(newUrl, file);
      setFormData((prev) => ({
        ...prev,
        packs: prev.packs?.map((pack, i) =>
          i === index ? { ...pack, imageUrl } : pack,
        ),
      }));
    } catch (error) {
      console.error("Failed to upload pack image:", error);
      toast({
        title: t("product.error_uploading_images"),
        description:
          error instanceof Error
            ? error.message
            : t("product.failed_to_upload_images"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploadingPackIndex(null);
    }
  };

  const removePackImage = async (index: number) => {
    if (formData.packs && formData.packs[index].imageUrl) {
      try {
        const mediaService = WSMedia.getInstance();
        await mediaService.deleteImage([formData.packs[index].imageUrl]);
        setFormData((prev) => ({
          ...prev,
          packs: prev.packs?.map((pack, i) =>
            i === index ? { ...pack, imageUrl: undefined } : pack,
          ),
        }));
      } catch (error) {
        console.error("Failed to delete pack image:", error);
        toast({
          title: t("error.deleteFailed"),
          description: error instanceof Error ? error.message : "Unknown error",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNumberChange = (name: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [name]: Number(value),
    }));
  };

  const handlePackChange = (
    index: number,
    field: keyof Pack,
    value: string | number,
  ) => {
    const updatedPacks = [...(formData.packs || [])];
    updatedPacks[index] = {
      ...updatedPacks[index],
      [field]: field === "name" ? value : Number(value),
    };
    setFormData((prev) => ({
      ...prev,
      packs: updatedPacks,
    }));
  };

  const addPack = () => {
    const newPack: Pack = {
      name: "",
      price: 0,
      discount: 0,
      quantity: 1,
      deliveryPrice: 0,
    };
    setFormData((prev) => ({
      ...prev,
      packs: [...(prev.packs || []), newPack],
    }));
  };

  const removePack = (index: number) => {
    const updatedPacks = [...(formData.packs || [])];
    updatedPacks.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      packs: updatedPacks,
    }));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".webp"],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    onDrop: async (acceptedFiles) => {
      if (!acceptedFiles.length) return;

      try {
        setIsLoading(true);

        const uploadPromises = acceptedFiles.map(async (file) => {
          const newUrl = await mediaInstance.getGeneratedURL();
          return mediaInstance.saveImage(newUrl, file);
        });

        const imageUrls = await Promise.all(uploadPromises);

        setFormData((prev) => ({
          ...prev,
          imageUrl: [...(prev.imageUrl || []), ...imageUrls],
        }));
      } catch (error) {
        toast({
          title: t("product.error_uploading_images"),
          description: t("product.failed_to_upload_images"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleRemoveImage = (url: string) => {
    setremovedImages([...removedImages, url]);
    setFormData((prev) => ({
      ...prev,
      imageUrl: prev.imageUrl?.filter((img) => img !== url),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let finalDescription = formData.description;

      const finalFormData = {
        ...formData,
        description: finalDescription || JSON.stringify({ blocks: [] }),
      };

      if (product && onUpdate) {
        const { createdAt, ...updateData } = finalFormData;
        await onUpdate({ ...updateData, updatedAt: new Date() });
        if (removedImages.length > 0) {
          await mediaInstance.deleteImage(removedImages);
        }
      } else {
        await onCreate(finalFormData);
      }

      toast({
        title: t("success"),
        description: product
          ? t("product.updated_successfully")
          : t("product.created_successfully"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      const action = product ? "updating" : "creating";
      toast({
        title: t("error") + " " + action + " product",
        description:
          error instanceof Error
            ? error.message
            : t("product.something_went_wrong"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size={"xl"}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          {product
            ? t("product.edit_product")
            : t("product.create_new_product")}
        </DrawerHeader>

        <DrawerBody>
          <Stack spacing={6}>
            {/* Product Images Section - Full Width */}
            <FormControl>
              <FormLabel> {t("product.images")}</FormLabel>
              <Box
                {...getRootProps()}
                border="2px dashed"
                borderColor={isDragActive ? "blue.400" : "gray.300"}
                borderRadius="lg"
                p={6}
                textAlign="center"
                cursor="pointer"
                bg={isDragActive ? "blue.50" : "transparent"}
                _hover={{
                  borderColor: "blue.400",
                  bg: "blue.50",
                }}
                transition="all 0.2s"
                mb={4}
              >
                <input {...getInputProps()} />
                <Flex direction="column" align="center" justify="center">
                  <Icon as={FiUpload} w={8} h={8} color="gray.400" mb={2} />
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    {t("product.drag_drop_images")}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {t("product.select_images")}
                  </Text>
                </Flex>
              </Box>

              {isLoading && (
                <Box textAlign="center" py={4}>
                  <Spinner size="md" color="blue.500" />
                  <Text mt={2} fontSize="sm" color="gray.500">
                    {t("product.uploading_images")}
                  </Text>
                </Box>
              )}

              {formData.imageUrl.length > 0 && (
                <Box>
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    {formData.imageUrl.length}{" "}
                    {formData.imageUrl.length === 1
                      ? t("product.image")
                      : t("product.images")}{" "}
                    {t("product.selected")}
                  </Text>
                  <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
                    {formData.imageUrl.map((url, index) => (
                      <Box
                        key={index}
                        position="relative"
                        borderRadius="md"
                        overflow="hidden"
                        border="1px solid"
                        borderColor="gray.200"
                        _hover={{
                          "& .remove-btn": {
                            opacity: 1,
                          },
                          "& img": {
                            transform: "scale(1.05)",
                          },
                        }}
                      >
                        <Box
                          as="img"
                          src={url}
                          alt={`Product ${index + 1}`}
                          w="100%"
                          h="120px"
                          objectFit="cover"
                          transition="transform 0.2s"
                        />
                        <IconButton
                          aria-label={t("product.remove_image")}
                          icon={<FiX />}
                          position="absolute"
                          top={2}
                          right={2}
                          size="sm"
                          colorScheme="red"
                          variant="solid"
                          opacity={0}
                          transition="opacity 0.2s"
                          className="remove-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage(url);
                          }}
                          isRound
                        />
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              )}
            </FormControl>

            {/* Two Column Layout for Form Fields */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {/* Left Column */}
              <Stack spacing={4}>
                {/* Product Name */}
                <FormControl isRequired>
                  <FormLabel>{t("product.name")}</FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t("product.enter_product_name")}
                  />
                </FormControl>

                {/* Product Reference */}
                <FormControl isRequired>
                  <FormLabel>{t("product.reference")}</FormLabel>
                  <Input
                    name="ref"
                    value={formData.ref}
                    onChange={handleChange}
                    placeholder={t("product.enter_product_reference")}
                  />
                </FormControl>

                {/* Product Cost */}
                <FormControl isRequired>
                  <FormLabel>{t("product.product_cost")}</FormLabel>
                  <NumberInput
                    value={formData.productCost}
                    min={0}
                    precision={2}
                    step={0.01}
                    onChange={(valueString, valueNumber) =>
                      handleNumberChange("productCost", valueNumber)
                    }
                  >
                    <NumberInputField placeholder="0.00" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                {/* Selling Price */}
                <FormControl isRequired>
                  <FormLabel>{t("product.selling_price")}</FormLabel>
                  <NumberInput
                    value={formData.price}
                    min={0}
                    precision={2}
                    onChange={(value) => handleNumberChange("price", value)}
                  >
                    <NumberInputField placeholder="0.00" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </Stack>

              {/* Right Column */}
              <Stack spacing={4}>
                {/* Discount Price */}
                <FormControl>
                  <FormLabel>{t("product.discount_price")}</FormLabel>
                  <NumberInput
                    value={formData.discount}
                    min={0}
                    precision={2}
                    onChange={(value) => handleNumberChange("discount", value)}
                  >
                    <NumberInputField placeholder="0.00" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                {/* Delivery Price */}
                <FormControl>
                  <FormLabel>{t("product.delivery_price")}</FormLabel>
                  <NumberInput
                    value={formData.deliveryPrice}
                    min={0}
                    precision={2}
                    onChange={(value) =>
                      handleNumberChange("deliveryPrice", value)
                    }
                  >
                    <NumberInputField placeholder="0.00" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                {/* Quantity */}
                <FormControl isRequired>
                  <FormLabel>{t("product.stock_quantity")}</FormLabel>
                  <NumberInput
                    value={formData.quantity}
                    min={0}
                    onChange={(value) => handleNumberChange("quantity", value)}
                  >
                    <NumberInputField placeholder="0" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                {/* In Stock Toggle */}
                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="in-stock" mb="0">
                    {t("product.in_stock")}
                  </FormLabel>
                  <Switch
                    id="in-stock"
                    isChecked={formData.inStock}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        inStock: e.target.checked,
                      }))
                    }
                  />
                </FormControl>
              </Stack>
            </SimpleGrid>

            {/* Tabs Section */}
            <Tabs index={activeTab} onChange={setActiveTab} colorScheme="blue">
              <TabList>
                <Tab>{t("product.description")}</Tab>
                <Tab>
                  {t("product.pack")}
                  {formData.packs && formData.packs.length > 0 && (
                    <Badge ml={2} colorScheme="blue" borderRadius="full">
                      {formData.packs.length}
                    </Badge>
                  )}
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <FormControl isRequired>
                    <FormLabel>{t("product.description")}</FormLabel>
                    <VEditor
                      key={formData.id}
                      value={formData.description || ""}
                      onChange={(value) =>
                        setFormData((prev) => ({ ...prev, description: value }))
                      }
                    />
                  </FormControl>
                </TabPanel>

                <TabPanel px={0}>
                  <Stack spacing={5}>
                    {/* Pack Name */}
                    <FormControl>
                      <FormLabel>{t("product.pack_name")}</FormLabel>
                      <Input
                        name="packName"
                        value={formData.packName || ""}
                        onChange={handleChange}
                        placeholder={t("product.enter_pack_name")}
                      />
                    </FormControl>

                    {/* Pack Items */}
                    <FormControl>
                      <Flex align="center" justify="space-between" mb={2}>
                        <FormLabel mb={0}>{t("product.pack_items")}</FormLabel>
                        <Button
                          leftIcon={<FiPlus />}
                          colorScheme="blue"
                          onClick={addPack}
                          size="sm"
                        >
                          {t("product.add_pack_item")}
                        </Button>
                      </Flex>

                      {(!formData.packs || formData.packs.length === 0) && (
                        <Flex
                          direction="column"
                          align="center"
                          justify="center"
                          py={10}
                          borderWidth={1}
                          borderStyle="dashed"
                          borderColor="gray.200"
                          borderRadius="lg"
                          bg="gray.50"
                        >
                          <Icon
                            as={FiPackage}
                            w={7}
                            h={7}
                            color="gray.400"
                            mb={2}
                          />
                          <Text fontSize="sm" color="gray.500">
                            {t("product.pack_items")}
                          </Text>
                        </Flex>
                      )}

                      <Stack spacing={4}>
                        {formData.packs?.map((pack, index) => (
                          <Box
                            key={index}
                            borderWidth="1px"
                            borderColor="gray.200"
                            borderRadius="lg"
                            overflow="hidden"
                            bg="white"
                            boxShadow="sm"
                            transition="box-shadow 0.15s"
                            _hover={{ boxShadow: "md" }}
                          >
                            {/* Card header */}
                            <Flex
                              align="center"
                              justify="space-between"
                              px={4}
                              py={2}
                              bg="gray.50"
                              borderBottomWidth="1px"
                              borderColor="gray.200"
                            >
                              <HStack spacing={2}>
                                <Badge
                                  colorScheme="blue"
                                  borderRadius="full"
                                  px={2}
                                >
                                  {index + 1}
                                </Badge>
                                <Text
                                  fontSize="sm"
                                  fontWeight="medium"
                                  color="gray.700"
                                >
                                  {pack.name ||
                                    t("product.enter_pack_item_name")}
                                </Text>
                              </HStack>
                              <IconButton
                                aria-label={t("product.remove_pack_item")}
                                icon={<FiTrash2 />}
                                colorScheme="red"
                                variant="ghost"
                                size="sm"
                                onClick={() => removePack(index)}
                              />
                            </Flex>

                            <Box p={4}>
                              <SimpleGrid
                                columns={{ base: 1, md: 2, lg: 3 }}
                                spacing={4}
                              >
                                <FormControl>
                                  <FormLabel fontSize="sm">
                                    {t("product.image")}
                                  </FormLabel>
                                  <PackImageUpload
                                    imageUrl={pack.imageUrl}
                                    isUploading={uploadingPackIndex === index}
                                    onUpload={(file) =>
                                      uploadPackImage(index, file)
                                    }
                                    onRemove={() => removePackImage(index)}
                                    t={t}
                                  />
                                </FormControl>

                                <FormControl>
                                  <FormLabel fontSize="sm">
                                    {t("product.pack_item_name")}
                                  </FormLabel>
                                  <Input
                                    value={pack.name}
                                    onChange={(e) =>
                                      handlePackChange(
                                        index,
                                        "name",
                                        e.target.value,
                                      )
                                    }
                                    placeholder={t(
                                      "product.enter_pack_item_name",
                                    )}
                                    size="sm"
                                  />
                                </FormControl>

                                <FormControl>
                                  <FormLabel fontSize="sm">
                                    {t("product.selling_price")}
                                  </FormLabel>
                                  <NumberInput
                                    value={pack.price}
                                    min={0}
                                    precision={2}
                                    onChange={(value) =>
                                      handlePackChange(index, "price", value)
                                    }
                                    size="sm"
                                  >
                                    <NumberInputField placeholder="0.00" />
                                    <NumberInputStepper>
                                      <NumberIncrementStepper />
                                      <NumberDecrementStepper />
                                    </NumberInputStepper>
                                  </NumberInput>
                                </FormControl>

                                <FormControl>
                                  <FormLabel fontSize="sm">
                                    {t("product.discount")}
                                  </FormLabel>
                                  <NumberInput
                                    value={pack.discount || 0}
                                    min={0}
                                    precision={2}
                                    onChange={(value) =>
                                      handlePackChange(index, "discount", value)
                                    }
                                    size="sm"
                                  >
                                    <NumberInputField placeholder="0.00" />
                                    <NumberInputStepper>
                                      <NumberIncrementStepper />
                                      <NumberDecrementStepper />
                                    </NumberInputStepper>
                                  </NumberInput>
                                </FormControl>

                                <FormControl>
                                  <FormLabel fontSize="sm">
                                    {t("product.quantity")}
                                  </FormLabel>
                                  <NumberInput
                                    value={pack.quantity || 1}
                                    min={1}
                                    onChange={(value) =>
                                      handlePackChange(index, "quantity", value)
                                    }
                                    size="sm"
                                  >
                                    <NumberInputField placeholder="1" />
                                    <NumberInputStepper>
                                      <NumberIncrementStepper />
                                      <NumberDecrementStepper />
                                    </NumberInputStepper>
                                  </NumberInput>
                                </FormControl>

                                <FormControl>
                                  <FormLabel fontSize="sm">
                                    {t("product.delivery_price")}
                                  </FormLabel>
                                  <NumberInput
                                    value={pack.deliveryPrice || 0}
                                    min={0}
                                    precision={2}
                                    onChange={(value) =>
                                      handlePackChange(
                                        index,
                                        "deliveryPrice",
                                        value,
                                      )
                                    }
                                    size="sm"
                                  >
                                    <NumberInputField placeholder="0.00" />
                                    <NumberInputStepper>
                                      <NumberIncrementStepper />
                                      <NumberDecrementStepper />
                                    </NumberInputStepper>
                                  </NumberInput>
                                </FormControl>
                              </SimpleGrid>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </FormControl>
                  </Stack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Stack>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px">
          <Button variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            type="submit"
            isLoading={isLoading}
            onClick={handleSubmit}
          >
            {product
              ? t("product.update_product")
              : t("product.create_product")}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
